/**
 * @typedef {Object} ProcessTemplateItem
 * @property {number} id
 * @property {string} name
 * @property {string} [description]
 * @property {number} tasks_count
 */

/**
 * @typedef {Object} ProcessTemplateSelectorProps
 * @property {ProcessTemplateItem[]} templates
 * @property {(templateId: number) => boolean} isSelected
 * @property {(templateId: number) => string|number} getPosition
 * @property {(templateId: number) => void} onToggle
 * @property {(templateId: number, position: number) => void} onPositionChange
 * @property {(templateId: number) => boolean} [isDisabled] - Optionnel, retourne true si le template doit être désactivé
 */

/**
 * @param {ProcessTemplateSelectorProps} props
 */
export default function ProcessTemplateSelector({
  templates,
  isSelected,
  getPosition,
  onToggle,
  onPositionChange,
  isDisabled = () => false,
}) {
  if (!templates.length) {
    return (
      <p className="text-sm text-base-content/60 italic py-2">
        Aucun template disponible pour ce type de process.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {templates.map((template) => {
        const selected = isSelected(template.id)
        const disabled = isDisabled(template.id)
        return (
          <div
            key={template.id}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
              disabled
                ? 'border-base-300 bg-base-200 opacity-60 cursor-not-allowed'
                : selected
                  ? 'border-primary bg-primary/5 cursor-pointer'
                  : 'border-base-300 hover:border-primary/50 cursor-pointer'
            }`}
            onClick={() => !disabled && onToggle(template.id)}
          >
            <input
              type="checkbox"
              className="checkbox"
              checked={selected}
              disabled={disabled}
              onChange={() => !disabled && onToggle(template.id)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm truncate">{template.name}</p>
                {disabled && (
                  <span className="badge badge-warning badge-xs shrink-0">En cours</span>
                )}
              </div>
              {template.description && (
                <p className="text-xs text-base-content/60 truncate">{template.description}</p>
              )}
              <p className="text-xs text-base-content/40">{template.tasks_count} tâche(s)</p>
            </div>
            {selected && (
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <label className="text-xs text-base-content/60">Ordre</label>
                <input
                  type="number"
                  min={1}
                  className="input input-bordered input-xs w-16 text-center"
                  value={getPosition(template.id)}
                  onChange={(e) => onPositionChange(template.id, parseInt(e.target.value, 10))}
                />
              </div>
            )}
            { disabled && (
              <div className="flex items-center gap-4 shrink-0">
                <label className="text-xs text-base-content/60">Ordre</label>
                <span className="text-xs w-16">{getPosition(template.id)}</span>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
